import { useEffect, useState } from 'react'
import cx from 'classnames'

import Styles from './RecipientSuggestion.module.scss'
import {
  RecipientSuggestionProps,
  SuggestionProps,
  UserSuggestionProps,
  ChannelSuggestionProps,
  AccessMemberGroupSuggestionProps,
  GroupInfo,
} from './Types'
import { AccessorAvatar } from 'components/ShareSheet/Access/AccessorAvatar'
import { ReactComponent as RightChevron } from 'assets/icons/chevronRight12.svg'
import { ReactComponent as DownChevron } from 'assets/icons/chevronDown12.svg'
import { useGroupMembers } from 'hooks/queries/groups/useGroupMembers'
import { RecipientsSelectorValueType } from 'types'
import { getInitials } from 'utils'

const Suggestion = ({ groupId, type, highlighted, avatar, title, description, ...props }: SuggestionProps) => {
  const [expanded, setExpanded] = useState(false)
  const [rowsToShow, setRowsToShow] = useState<GroupInfo[]>([])
  const { data } = useGroupMembers({ groupId: groupId, enabled: expanded && !!groupId })

  useEffect(() => {
    if (!data?.length) return

    const rows = data.map((membership) => {
      const {
        member: { id, avatar_url, display_name },
      } = membership
      return { id: id, avatar_url: avatar_url || '', name: display_name, type: RecipientsSelectorValueType.User }
    })

    setRowsToShow(rows)
  }, [data])

  return (
    <>
      <div className={cx(Styles.root, { [Styles.highlighted]: highlighted })} {...props}>
        <AccessorAvatar size={24} avatarUrl={avatar.url || ''} name={avatar.label} type={type} />
        <div className={Styles.info}>
          <div className={Styles.titleContainer}>
            <div className={Styles.title}>{title}</div>
            {!!groupId && (
              <div
                className={Styles.chevron}
                onMouseDown={(e) => {
                  e.stopPropagation()
                  setExpanded((prev) => !prev)
                }}
              >
                {expanded ? <DownChevron /> : <RightChevron />}
              </div>
            )}
          </div>
          <div className={Styles.description}>{description}</div>
        </div>
      </div>
      <div className={cx(Styles.expandedContents, { [Styles.showing]: expanded })}>
        {rowsToShow.map((row) => (
          <Suggestion
            key={row.id}
            avatar={{ url: row.avatar_url, label: getInitials(row.name) }}
            title={row.name}
            description={''}
            type={row.type}
            highlighted={false}
          />
        ))}
      </div>
    </>
  )
}

const UserSuggestion = ({ recipient, ...props }: UserSuggestionProps) => {
  const { avatar_url, email, display_name } = recipient
  const title = display_name
  const description = display_name === email ? '' : email
  const avatarLabel = getInitials(display_name)
  return (
    <Suggestion
      type={RecipientsSelectorValueType.User}
      avatar={{ url: avatar_url, label: avatarLabel }}
      title={title}
      description={description}
      {...props}
    />
  )
}

const ChannelSuggestion = ({ recipient, ...props }: ChannelSuggestionProps) => {
  const { name, members_count } = recipient
  return (
    <Suggestion
      groupId={recipient.id}
      type={RecipientsSelectorValueType.Channel}
      avatar={{ label: getInitials(name) }}
      title={name}
      description={`${members_count} ${members_count > 1 ? 'members' : 'member'}`}
      {...props}
    />
  )
}

const AccessMemberGroupSuggestion = ({ recipient, ...props }: AccessMemberGroupSuggestionProps) => {
  const { label, name, members } = recipient
  return (
    <Suggestion
      avatar={{ label: getInitials(label) }}
      title={name}
      type={RecipientsSelectorValueType.AccessMemberGroup}
      description={`${members.length} ${members.length > 1 ? 'members' : 'member'}`}
      {...props}
    />
  )
}

const RecipientSuggestion = ({ recipient, ...props }: RecipientSuggestionProps) => {
  switch (recipient.type) {
    case RecipientsSelectorValueType.User:
      return <UserSuggestion recipient={recipient.value} {...props} />
    case RecipientsSelectorValueType.Channel:
      return <ChannelSuggestion recipient={recipient.value} {...props} />
    case RecipientsSelectorValueType.AccessMemberGroup:
      return <AccessMemberGroupSuggestion recipient={recipient.value} {...props} />
    default:
      return null
  }
}

export { RecipientSuggestion }
